// SPDX-License-Identifier: AGPL-3.0-only
/**
 * First-run setup API client: GET /api/v1/setup/state and POST
 * /api/v1/setup/super-admin. Shapes mirror apps/server
 * src/routes/setup/schema.ts (type-only copy per the matrix — the dashboard
 * may not import server runtime code). Change both together.
 */
import { queryOptions } from '@tanstack/react-query';

import { api, setCsrfToken } from '../app/api.js';

export interface SetupState {
  /** True only on a never-bootstrapped instance (zero users AND no claim). */
  required: boolean;
  passwordMinLength: number;
}

/** The two outbound-call consents. Both default OFF (v0.5 exit criterion). */
export interface SetupConsent {
  telemetry: boolean;
  updateCheck: boolean;
}

export interface SetupSuperAdminInput {
  email: string;
  password: string;
  name?: string | undefined;
  consent: SetupConsent;
}

export interface SetupUser {
  id: string;
  email: string;
  name: string;
}

export const SETUP_STATE_QUERY_KEY = ['setup', 'state'] as const;

export function setupStateQuery() {
  return queryOptions({
    queryKey: SETUP_STATE_QUERY_KEY,
    queryFn: async () => (await api.get<{ data: SetupState }>('/api/v1/setup/state')).data,
    // Setup state is a one-way door (required: true → false, never back), and
    // the route guards read it on every navigation — cache it for the session.
    staleTime: Infinity,
  });
}

/**
 * Creates the one-and-only super admin. Resolves with the created user and a
 * session cookie already set (the server signs the wizard in). A 409 means
 * setup was already completed — the caller must send the user to /login rather
 * than retry, since no retry can ever succeed.
 *
 * ─── WHY THIS WRITES THE CSRF HOLDER ─────────────────────────────────────────
 *
 * This call MINTS a session, and from the instant it resolves every mutation
 * this tab makes carries an ambient credential — so `security/csrf.ts` starts
 * demanding the session-bound token (`app/api.ts`). `GET /bootstrap` is the
 * other place that issues it, and the self-host wizard reaches it for free by
 * navigating into `appRoute` on success. The DESKTOP wizard does not: it stays
 * on `/desktop/setup` (a child of the router root, because there is no account
 * to bootstrap as when it loads) and goes straight on to create a database,
 * introspect it and generate pages. Those three calls were 403ing, and the
 * first symptom was the first-run wizard never reaching "Generate dashboard".
 *
 * So the token comes back in this reply and is installed here — in the shared
 * client both wizards call, rather than in either wizard — because the thing
 * that needs it is the SESSION being created, which is this function's doing.
 */
export async function createSuperAdmin(input: SetupSuperAdminInput): Promise<SetupUser> {
  const body = await api.post<{ data: { user: SetupUser; csrfToken?: string } }>(
    '/api/v1/setup/super-admin',
    {
      email: input.email,
      password: input.password,
      ...(input.name === undefined || input.name.length === 0 ? {} : { name: input.name }),
      consent: input.consent,
    },
  );
  // Optional on the wire only so fixtures predating the field keep typechecking
  // (the server's Zod reply schema makes it required), exactly as
  // `app/bootstrap.ts` treats its own copy.
  setCsrfToken(body.data.csrfToken ?? null);
  return body.data.user;
}

/**
 * `POST /setup/probe` — does this database already run an Adminium?
 *
 * Asked from the CONNECT step, before there is an account to ask with: the
 * route is open only while `setup.state.required` is true and closes forever
 * after the first one (`server/src/routes/setup/store.ts`). Advisory by design
 * — a database that will not answer comes back `reachable: false` rather than
 * throwing, because the wizard tests the DSN properly once a session exists.
 */
export interface SetupProbe {
  reachable: boolean;
  reason: string | null;
  tables: string[];
  /** The subset holding rows — what a relocation actually refuses over. */
  occupied: string[];
  /** `null` when the store holds nothing encrypted to test against. */
  secretMatches: boolean | null;
}

export async function probeStore(dsn: string): Promise<SetupProbe> {
  return (await api.post<{ data: SetupProbe }>('/api/v1/setup/probe', { dsn })).data;
}

/**
 * `POST /setup/adopt` — point this instance at a store that already exists.
 *
 * Nothing is copied and nothing is dropped: the bootstrap file is written and
 * the server restarts onto it. The reply is the health path to wait on, exactly
 * as `/meta/relocate` answers — a resolved promise means the file is written,
 * not that the server is back.
 */
export interface SetupAdopted {
  engine: string;
  restarting: boolean;
  healthPath: string;
}

export async function adoptStore(dsn: string): Promise<SetupAdopted> {
  return (await api.post<{ data: SetupAdopted }>('/api/v1/setup/adopt', { dsn })).data;
}
